import { ReactElement, use, useCallback, useEffect, useRef, useState } from 'react';
import { confirmable, ConfirmDialog, createConfirmation } from 'react-confirm';
import styled from 'styled-components';
import { Copyright } from '../../../common/src/model.ts';
import * as server_api from '../../../common/src/server_api.ts';
import { useHousehold } from '../data/use_household.tsx';
import { SequencerContext } from '../player/sequencer.tsx';
import { Button } from './onboarding/button.tsx';
import { Message } from './onboarding/common.tsx';

export interface Props {
  isDialog?: boolean;
  isMidiFileMissing?: boolean;
  onDisplayPDF?: () => void;
}

export function RequestSongPage({ isDialog, isMidiFileMissing, onDisplayPDF }: Props) {
  const [requested, setRequested] = useState(false);
  const { hymn, copyrightIssues } = use(SequencerContext);
  const { household } = useHousehold();

  let message: ReactElement;
  let allowPDF;
  if (isMidiFileMissing || !copyrightIssues) {
    message = (
      <span>
        We&apos;ll add this song soon!
        <br />
        Request it to help us prioritize.
      </span>
    );
    allowPDF = true;
  } else if (copyrightIssues.includes(Copyright.Music)) {
    message = <span>The copyright holder has not yet granted permission for this song.</span>;
    allowPDF = false;
  } else {
    message = <span>The copyright holder has not yet granted permission for the text.</span>;
    allowPDF = false;
    if (!copyrightIssues.includes(Copyright.Text)) {
      console.error(`Failed to cover all Copyright values ${JSON.stringify(copyrightIssues)}`);
    }
  }

  const onRequestSong = useCallback(() => {
    if (requested || !hymn || !household) {
      return;
    }
    setRequested(true);
    void server_api.addSongRequest({
      email: household.householdEmail,
      hymnalName: hymn.hymnal,
      songNumber: hymn.songNumber,
      timestamp: Date.now(),
      issue: copyrightIssues?.includes(Copyright.Music) ? Copyright.Music : Copyright.Text,
    });
  }, [requested, hymn, household, copyrightIssues]);

  return (
    <RequestSongWrapper $isDialog={Boolean(isDialog)}>
      <RequestSongMessage $widthInChars={24} $isDialog={isDialog}>
        {message}
      </RequestSongMessage>
      <RequestButtonWrapper>
        <Button onClick={onRequestSong} $requested={requested}>
          {requested ? (
            <span>
              <b>✓</b> REQUESTED
            </span>
          ) : (
            'REQUEST SONG'
          )}
        </Button>
      </RequestButtonWrapper>
      {allowPDF && onDisplayPDF && <Button onClick={onDisplayPDF}>VIEW PDF</Button>}
    </RequestSongWrapper>
  );
}

const RequestSongMessage = styled(Message)<{ $isDialog?: boolean }>`
  font-size: 1em;
  color: var(--color-pdf-text);
  text-align: center;
`;

// TODO(hewitt): Some serious CSS hacking here :/ - ideally, the buttons would be bottom-aligned like onboarding.
//               However, the player page header forces them down off the screen.
const RequestSongWrapper = styled.div<{ $isDialog: boolean }>`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => (props.$isDialog ? 'transparent' : 'var(--color-pdf-background);')};
  pointer-events: none;
`;

const RequestButtonWrapper = styled.div`
  width: 25ch;
  display: flex;
  justify-content: center;
  pointer-events: all;
`;

interface DialogProps {
  confirmation: Props;
}

const RequestSongPageDialog: ConfirmDialog<DialogProps, boolean> = ({ confirmation, proceed, show }) => {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    if (show) {
      dialogRef.current?.showModal();
    } else {
      dialogRef.current?.close();
    }
  }, [show, dialogRef]);
  const onDisplayPDF = useCallback(() => {
    proceed(false);
  }, [proceed]);
  return (
    <Dialog ref={dialogRef}>
      <RequestSongPage isMidiFileMissing={confirmation.isMidiFileMissing} onDisplayPDF={onDisplayPDF} isDialog={true} />
    </Dialog>
  );
};

export const requestSongPageDialog = createConfirmation(confirmable(RequestSongPageDialog));

const Dialog = styled.dialog`
  color: var(--color-text);
  background-color: var(--color-background);
  border: none;
  border-radius: 20px;
  position: fixed;
  margin: auto;
`;
